import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconBell,
  IconClose,
  IconHeart,
  IconTrash,
} from '../icons';
import './NotificationTray.scss';

const IconNotificationComment = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
    <path d="M14.3019 6.68287C14.3019 8.23007 13.7298 9.66025 12.7677 10.7914C11.4805 12.3516 9.43922 13.3527 7.15093 13.3527L3.75749 15.368C3.18541 15.719 2.45732 15.238 2.53533 14.5749L2.86037 12.0136C1.11814 10.8044 0 8.86715 0 6.68287C0 4.39457 1.22216 2.37931 3.0944 1.18315C4.25155 0.429055 5.64273 0 7.15093 0C11.1034 0 14.3019 2.99039 14.3019 6.68287Z" fill="url(#paint0_linear_10904_34101)" />
    <defs>
      <linearGradient id="paint0_linear_10904_34101" x1="7.15113" y1="19.0998" x2="7.15099" y2="-5.65354e-07" gradientUnits="userSpaceOnUse">
        <stop stopColor="#77C075" />
        <stop offset="1" stopColor="#FFFECC" />
      </linearGradient>
    </defs>
  </svg>
);

const IconNotificationSparkle = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M9.18022 2.34488C9.21593 2.15372 9.31737 1.98106 9.46697 1.85681C9.61658 1.73256 9.80492 1.66455 9.99939 1.66455C10.1939 1.66455 10.3822 1.73256 10.5318 1.85681C10.6814 1.98106 10.7829 2.15372 10.8186 2.34488L11.6944 6.97655C11.7566 7.30584 11.9166 7.60873 12.1536 7.84569C12.3905 8.08265 12.6934 8.24267 13.0227 8.30488L17.6544 9.18071C17.8456 9.21642 18.0182 9.31786 18.1425 9.46746C18.2667 9.61706 18.3347 9.80541 18.3347 9.99988C18.3347 10.1943 18.2667 10.3827 18.1425 10.5323C18.0182 10.6819 17.8456 10.7833 17.6544 10.819L13.0227 11.6949C12.6934 11.7571 12.3905 11.9171 12.1536 12.1541C11.9166 12.391 11.7566 12.6939 11.6944 13.0232L10.8186 17.6549C10.7829 17.846 10.6814 18.0187 10.5318 18.1429C10.3822 18.2672 10.1939 18.3352 9.99939 18.3352C9.80492 18.3352 9.61658 18.2672 9.46697 18.1429C9.31737 18.0187 9.21593 17.846 9.18022 17.6549L8.30439 13.0232C8.24219 12.6939 8.08216 12.391 7.8452 12.1541C7.60824 11.9171 7.30535 11.7571 6.97606 11.6949L2.34439 10.819C2.15323 10.7833 1.98057 10.6819 1.85632 10.5323C1.73208 10.3827 1.66406 10.1943 1.66406 9.99988C1.66406 9.80541 1.73208 9.61706 1.85632 9.46746C1.98057 9.31786 2.15323 9.21642 2.34439 9.18071L6.97606 8.30488C7.30535 8.24267 7.60824 8.08265 7.8452 7.84569C8.08216 7.60873 8.24219 7.30584 8.30439 6.97655L9.18022 2.34488Z" fill="white" />
  </svg>
);

const IconNotificationLibrary = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M8.33333 2.5H3.33333C2.8731 2.5 2.5 2.8731 2.5 3.33333V16.6667C2.5 17.1269 2.8731 17.5 3.33333 17.5H8.33333C8.79357 17.5 9.16667 17.1269 9.16667 16.6667V3.33333C9.16667 2.8731 8.79357 2.5 8.33333 2.5Z" fill="white" />
    <path d="M5.83333 2.5V17.5V2.5Z" fill="white" />
    <path d="M17 15.75C17.1667 16.1667 16.9167 16.6667 16.5 16.8333L14.9167 17.4167C14.5 17.5833 14 17.3333 13.8333 16.9167L9.25 4.25C9.08333 3.83333 9.33333 3.33333 9.75 3.16667L11.3333 2.58333C11.75 2.41667 12.25 2.66667 12.4167 3.08333L17 15.75Z" fill="white" />
  </svg>
);

const TYPE_ICONS = {
  like: <IconHeart size={20} filled />,
  comment: <IconNotificationComment />,
  collect: <IconNotificationLibrary />,
  memory: <IconNotificationSparkle />,
  follow: <IconNotificationSparkle />,
};

function getInitials(name = '') {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'SV';
}

function hasBodyCopy(notification) {
  return Boolean(notification.title && notification.description);
}

function isExpanded(notification) {
  const line = getLine(notification);
  return (
    hasBodyCopy(notification) ||
    Boolean(notification.previewImage) ||
    Boolean(line.length > 42) ||
    Boolean(notification.description?.length > 42)
  );
}

function getLine(notification) {
  if (notification.line) return notification.line;
  return `${notification.action || notification.title || ''}${notification.target ? ` ${notification.target}` : ''}`;
}

function AvatarCluster({ notification }) {
  const images = notification.avatarImages || (notification.avatarImage ? [notification.avatarImage] : []);

  if (images.length > 0) {
    return (
      <div className={`notification-tray__avatar notification-tray__avatar--${images.length > 1 ? 'stack' : 'single'} notif-panel__avatar notif-panel__avatar--${notification.type || 'default'}`} aria-hidden="true">
        {images.slice(0, 2).map((image, index) => (
          <img key={`${image}-${index}`} src={image} alt="" />
        ))}
      </div>
    );
  }

  return (
    <div className={`notification-tray__avatar notification-tray__avatar--initials${notification.avatarStack ? ' notification-tray__avatar--stack' : ''}`} aria-hidden="true">
      <span>{getInitials(notification.user || notification.title)}</span>
    </div>
  );
}

function PreviewStack({ image }) {
  return (
    <span className="notification-tray__preview" aria-hidden="true">
      <img className="notification-tray__preview-back" src={image} alt="" />
      <img className="notification-tray__preview-front" src={image} alt="" />
    </span>
  );
}

function NotificationRow({ notification, onDismiss, onSelect }) {
  const hasPreview = Boolean(notification.previewImage);
  const selectable = Boolean(onSelect);

  const handleSelect = () => {
    onSelect?.(notification);
  };

  const handleKeyDown = (event) => {
    if (!selectable) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect();
    }
  };

  return (
    <article
      className={`notification-tray__item${notification.unread ? ' notification-tray__item--unread' : ''}${isExpanded(notification) ? ' notification-tray__item--expanded' : ''}${hasPreview ? ' notification-tray__item--preview' : ''}${selectable ? ' notification-tray__item--selectable' : ''}`}
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      onClick={selectable ? handleSelect : undefined}
      onKeyDown={handleKeyDown}
    >
      <div className={`notification-tray__icon notification-tray__icon--${notification.type}`}>
        {TYPE_ICONS[notification.type] || <IconBell size={20} />}
      </div>

      <div className="notification-tray__content">
        <AvatarCluster notification={notification} />

        <div className="notification-tray__copy">
          {hasBodyCopy(notification) ? (
            <div className="notification-tray__text-group">
              <p className="notification-tray__title notification-tray__title--standalone">
                {notification.title}
              </p>
              <p className="notification-tray__description">{notification.description}</p>
            </div>
          ) : (
            <p className="notification-tray__title">
              {notification.user ? <strong>{notification.user}</strong> : null}
              {notification.user ? ' ' : ''}
              {getLine(notification)}
            </p>
          )}
          <span className="notification-tray__date">{notification.date || notification.time}</span>
        </div>
      </div>

      {hasPreview ? <PreviewStack image={notification.previewImage} /> : null}

      <button
        className="notification-tray__dismiss"
        type="button"
        aria-label={`Dismiss ${notification.user || notification.title || 'notification'}`}
        onClick={(event) => {
          event.stopPropagation();
          onDismiss?.(notification.id);
        }}
      >
        <IconTrash size={20} />
      </button>
    </article>
  );
}

export default function NotificationTray({
  notifications = [],
  onClose,
  onDismiss,
  onSelect,
  className = '',
  mode = 'panel',
  role,
  headingId = 'notifications-heading',
}) {
  const scrollerRef = useRef(null);
  const [scrollbar, setScrollbar] = useState({ visible: false, height: 48, top: 0 });

  const syncScrollbar = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const { clientHeight, scrollHeight, scrollTop } = scroller;
    if (scrollHeight <= clientHeight + 1) {
      setScrollbar((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }

    const trackHeight = clientHeight - 16;
    const thumbHeight = Math.max(48, Math.round((clientHeight / scrollHeight) * trackHeight));
    const maxTop = Math.max(0, trackHeight - thumbHeight);
    const top = Math.round((scrollTop / (scrollHeight - clientHeight)) * maxTop);
    setScrollbar({ visible: true, height: thumbHeight, top });
  }, []);

  useEffect(() => {
    syncScrollbar();
    window.addEventListener('resize', syncScrollbar);
    return () => window.removeEventListener('resize', syncScrollbar);
  }, [notifications, syncScrollbar]);

  const rootClass = [
    'notification-tray',
    `notification-tray--${mode}`,
    scrollbar.visible ? 'notification-tray--scrollable' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <section className={rootClass} role={role} aria-labelledby={headingId}>
      <header className="notification-tray__header">
        <h2 id={headingId} className="notification-tray__heading">Notifications</h2>
        <button className="notification-tray__close" type="button" onClick={onClose} aria-label="Close notifications">
          <IconClose size={20} />
        </button>
      </header>

      {notifications.length === 0 ? (
        <div className="notification-tray__empty" role="status">
          <span className="notification-tray__empty-title">No notifications yet</span>
          <span className="notification-tray__empty-copy">
            Vista will surface collectors, comments, and room activity here.
          </span>
        </div>
      ) : (
        <div
          className="notification-tray__scroller"
          ref={scrollerRef}
          role="region"
          aria-label="Notifications tray"
          onScroll={syncScrollbar}
        >
          <div className="notification-tray__list">
            {notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onDismiss={onDismiss}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      )}

      {scrollbar.visible ? (
        <div className="notification-tray__scrollbar" aria-hidden="true">
          <span style={{ height: `${scrollbar.height}px`, transform: `translateY(${scrollbar.top}px)` }} />
        </div>
      ) : null}
    </section>
  );
}
